import React, { useEffect, useState, useRef } from "react";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import { useToasts } from "react-toast-notifications";
import { colors } from "../../../helpers/colors";
import { FLAT_INCLINED_ALERT } from "../utils/horizontalTrace";
import { FLAT_PARALLEL_NOTE, ALPHA0_STRIKE_NOTE, alphaBetween } from "../../shared/alphaZero";
import { withDshapeSection } from "../utils/section";
import { inputTraceAlerts } from "../../shared/inputTraceAlerts";
import {
  ORIENTATION_SOURCE_OPTIONS,
  FACELOG_FIELD_NAMES,
  TRACE_CHAINAGE_FIELD_NAMES,
  FACE_POINT_FIELD_NAMES,
  isVerticalTrace,
  faceLogInputsReady,
  chainageInputsReady,
  computeOrientationFromFaceLogs,
  computeOrientationFromChainages,
  deriveDataLocation,
  sourceDerivesLocation,
  traceChainageRangeError,
  FACE_POINT_ON_CENTRELINE_ALERT,
} from "../../shared/orientationSources";
import { exceedsMaxLogLength, LOG_LENGTH_ALERT, isShortLog, shortLogError } from "../../shared/chainage";
import { BOX_SECTION_ALERT, effectiveWallHeight } from "../../shared/section";
import { LABEL_FIELD, finalLocationField } from "../../shared/labelField";
import "../../shared/GuidedInput.css";
import ConfirmResetModal from "../../shared/ConfirmResetModal";

/**
 * Input Component - Guided Input Pattern
 * Shows all fields visible with step-by-step guidance
 * Active field: Bold + blinking "Input Please"
 * Completed field: Normal text with checkmark
 */
const Input = (props) => {
  let { addToast, removeToast } = useToasts();
  const [currentDimension, setCurrentDimension] = useState({});
  const [activeFieldIndex, setActiveFieldIndex] = useState(0);
  const [completedFields, setCompletedFields] = useState(new Set());
  const [heightConfirm, setHeightConfirm] = useState(false);
  const [widthConfirm, setWidthConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(props.isEditInput || false);
  const inputRefs = useRef({});
  // The error toast each field last raised, cleared once that field passes.
  const fieldErrorToasts = useRef({});

  // Field configuration for D-Shape tunnel
  const shapeFields = [
    { id: "direction", name: "direction", label: "Tunnel Orientation (°)", type: "number", step: 1 },
    { id: "inclination", name: "inclination", label: "Tunnel Inclination (°)", type: "text", inputMode: "text", step: 1 },
    { id: "height", name: "height", label: "Tunnel Height", type: "number", step: 0.1 },
    { id: "width", name: "width", label: "Tunnel Width", type: "number", step: 0.1 },
    { id: "hw", name: "hw", label: "Tunnel Wall Height", type: "number", step: 0.1 },
  ];

  // Asked on the first trace for every source; the chainages a source carries
  // are then validated against it.
  const extentFields = [
    { id: "startrd", name: "startrd", label: "Start Chainage (m) of Tunnel", type: "number", step: 0.1 },
    { id: "endrd", name: "endrd", label: "End Chainage (m) of Tunnel", type: "number", step: 0.1 },
  ];

  // Everything that is fixed for the whole log and therefore read-only once
  // the first trace has set it
  const geometryFields = [...shapeFields, ...extentFields];

  // The source is chosen on its own screen before this form; the form reports
  // the choice and offers a way back, but never asks.
  const sourceLabel = (
    ORIENTATION_SOURCE_OPTIONS.find(
      (o) => o.value === (currentDimension.orientationsource || 'known')
    ) || {}
  ).label;

  // Algorithm_FaceLog3D_Rev.2_23Aug26 inputs (Tunnel Direction / Inclination
  // are reused from the geometry fields above).
  //
  // The apparent dip comes before the per-face points because it decides what
  // those points need to carry: a vertical trace (90) has no measurable height
  // and no left/right sense, so Rev.2 asks for neither.
  const faceLogVertical = isVerticalTrace(currentDimension.fappdip);

  const SIDE_OPTIONS = [
    { value: '', label: 'Select side' },
    { value: 'left', label: 'Left of Centreline' },
    { value: 'right', label: 'Right of Centreline' },
    { value: 'center', label: 'On the Centreline' },
  ];

  // Rev.2 lists the distance before the side; they are swapped here because
  // choosing the centreline is what fixes the distance at zero, and a field
  // cannot be settled by an answer the user has not given yet.
  const facePointFields = (n) => {
    const centred = currentDimension[`fside${n}`] === 'center';
    return [
      {
        id: `fside${n}`,
        name: `fside${n}`,
        label: `Face-${n}: Side of Known Point`,
        type: 'select',
        options: SIDE_OPTIONS,
      },
      {
        id: `fd${n}`,
        name: `fd${n}`,
        label: `Face-${n}: Distance of Known Point from Centreline`,
        type: 'number',
        step: 0.01,
        // On the centreline the distance is 0 and not editable.
        readOnly: centred,
      },
      ...(faceLogVertical
        ? []
        : [{
            id: `fh${n}`,
            name: `fh${n}`,
            label: `Face-${n}: Height of Known Point above Invert`,
            type: 'number',
            step: 0.01,
          }]),
    ];
  };

  const faceLogFields = [
    { id: 'fch1', name: 'fch1', label: 'Face-1 Chainage (m) (Lower Chainage)', type: 'number', step: 0.01 },
    { id: 'fch2', name: 'fch2', label: 'Face-2 Chainage (m) (Higher Chainage)', type: 'number', step: 0.01 },
    { id: 'fappdip', name: 'fappdip', label: 'Apparent Dip of Discontinuity on Face-1', type: 'number', step: 1 },
    ...(faceLogVertical
      ? []
      : [{
          id: 'ftracedir',
          name: 'ftracedir',
          label: 'Trace Direction on Face-1',
          type: 'select',
          options: [
            { value: '', label: 'Select direction' },
            { value: 'risesright', label: 'Rises towards Right' },
            { value: 'fallsright', label: 'Falls towards Right' },
          ],
        }]),
    ...facePointFields(1),
    ...facePointFields(2),
  ];

  // Known chainages of the discontinuity trace (same inputs as the Computing
  // Discontinuity Orientation activity; tunnel data reused from above)
  const traceChainageFields = [
    { id: 'p4', name: 'p4', label: 'Ch. Left SPL', type: 'number', step: 0.01 },
    { id: 'p2', name: 'p2', label: 'Ch. Right SPL', type: 'number', step: 0.01 },
    { id: 'p5', name: 'p5', label: 'Ch. Centreline (at Crown)', type: 'number', step: 0.01 },
  ];

  // Under a computed source the orientation is an output the form happens to
  // hold, so it is labelled and locked as one.
  const orientationIsComputed = sourceDerivesLocation(
    currentDimension.orientationsource || 'known'
  );
  const dipLabel = (what) =>
    orientationIsComputed ? `Computed Input - Dip ${what}` : `Dip ${what}`;

  const discontinuityFields = [
    {
      id: 'dir',
      name: 'dir',
      label: dipLabel('Direction'),
      type: 'number',
      step: 1,
      readOnly: orientationIsComputed,
    },
    {
      id: 'amt',
      name: 'amt',
      label: dipLabel('Amount'),
      type: 'number',
      step: 1,
      readOnly: orientationIsComputed,
    },
    {
      id: 'orientation',
      name: 'orientation',
      label: "Discontinuity data location",
      type: 'select',
      options: [
        { value: '', label: 'Select value' },
        { value: 'leftinvert', label: 'Left Invert' },
        { value: 'leftspl', label: 'Left Spring Level (SPL)' },
        { value: 'rightinvert', label: 'Right Invert' },
        { value: 'rightspl', label: 'Right Spring Level (SPL)' },
        { value: 'centerline', label: 'Tunnel Centerline at Crown' },
        { value: 'otherpoint', label: 'Any Other Point' },
      ]
    },
    { id: 'rd', name: 'rd', label: 'Chainage (m)', type: 'number', step: 0.01, dynamicLabel: true },
  ];

  const p6Fields = [
    {
      id: 'tunnelside',
      name: 'tunnelside',
      label: 'Side of Tunnel',
      type: 'select',
      options: [
        { value: '', label: 'Select side' },
        { value: 'left', label: 'Left Side' },
        { value: 'right', label: 'Right Side' },
        { value: 'center', label: 'Center' },
      ]
    },
    {
      id: 'distancefromcenter',
      name: 'distancefromcenter',
      label: 'Distance from Centerline',
      type: 'number',
      step: 0.01,
      // On the centreline the distance is 0 and not editable.
      readOnly: currentDimension.tunnelside === 'center',
    },
    { id: 'verticalheight', name: 'verticalheight', label: 'Vertical Height from Invert', type: 'number', step: 0.01 },
    { id: 'rd', name: 'rd', label: 'Chainage (m) of Discontinuity', type: 'number', step: 0.01 },
  ];

  // Get visible fields based on trace and orientation
  const getVisibleFields = () => {
    const source = currentDimension.orientationsource || 'known';
    const derived = sourceDerivesLocation(source);
    let fields = [];

    // For first trace, include the tunnel shape
    if (props.currenttrace === 0) {
      fields = [...shapeFields];
    }

    if (props.currenttrace === 0) {
      fields = [...fields, ...extentFields];
    }

    // The source (chosen on the previous screen) decides what comes next.
    if (source === 'facelogs') {
      fields = [...fields, ...faceLogFields];
    } else if (source === 'chainages') {
      fields = [...fields, ...traceChainageFields];
    }

    // Dip Direction / Amount stay visible for every source — computed ones are
    // filled in automatically but remain editable
    fields = [...fields, ...discontinuityFields.filter(f => f.id !== 'rd' && f.id !== 'orientation')];

    // The data location is an input only for Known Orientation; the other two
    // sources already fix it, and asking again invites a contradiction.
    if (!derived) {
      fields = [...fields, ...discontinuityFields.filter(f => f.id === 'orientation')];
      if (currentDimension.orientation === 'otherpoint') {
        fields = [...fields, ...p6Fields];
      } else {
        fields = [...fields, ...discontinuityFields.filter(f => f.id === 'rd')];
      }
    }

    return [...fields, LABEL_FIELD];
  };

  const visibleFields = getVisibleFields();
  const activeField = visibleFields[activeFieldIndex];

  /** Locked: computed for the user, or fixed by the first trace. */
  const isFieldReadOnly = (field) =>
    Boolean(field.readOnly) ||
    (props.currenttrace > 0 && geometryFields.some((f) => f.id === field.id));

  useEffect(() => {
    let temp = { ...props.dimensions[props.currenttrace] };
    setCurrentDimension(temp);
  }, [props.currenttrace, props.dimensions]);

  // A read-only field takes no input, so it completes itself and the guidance
  // moves on. Without this the walk stalls on a value the user cannot type:
  // Create Trace stays disabled until every field is complete, and a computed
  // Dip Amount at the end of the form left no way to finish.
  useEffect(() => {
    if (isEditMode || !activeField) return;
    if (!isFieldReadOnly(activeField)) return;
    if (completedFields.has(activeField.id)) return;
    if (!validateField(activeField.id)) return;
    setCompletedFields((prev) => new Set([...prev, activeField.id]));
    setActiveFieldIndex((i) => Math.min(i + 1, visibleFields.length - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField && activeField.id, isEditMode, completedFields]);

  // Auto-focus active field; scroll only when out of view (smoother UX)
  useEffect(() => {
    if (!activeField || !inputRefs.current[activeField.id]) return;
    const el = inputRefs.current[activeField.id];
    const scrollPadding = 80;
    requestAnimationFrame(() => {
      el.focus();
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= scrollPadding && rect.bottom <= window.innerHeight - scrollPadding;
      if (!inView && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    });
  }, [activeFieldIndex, activeField]);

  // Reset completed fields when trace changes
  useEffect(() => {
    setCompletedFields(new Set());
    setActiveFieldIndex(0);
    setIsEditMode(false);
  }, [props.currenttrace]);

  // Initialize edit mode when returning from Output via "Edit Input"
  useEffect(() => {
    if (props.isEditInput) {
      setIsEditMode(true);
      setCompletedFields(new Set(visibleFields.map(f => f.id)));
    }
  }, [props.isEditInput]);

  // Clamp activeFieldIndex when visible fields change (e.g. orientation -> otherpoint)
  useEffect(() => {
    if (activeFieldIndex >= visibleFields.length && visibleFields.length > 0) {
      setActiveFieldIndex(Math.max(0, visibleFields.length - 1));
    }
  }, [visibleFields.length, activeFieldIndex]);

  const updateCurrentTrace = (e) => {
    props.setcurrenttrace(Number(e.target.value));
  };

  const toRadians = (angle) => angle * (Math.PI / 180);
  const toDegrees = (radian) => radian * (180 / Math.PI);

  const calculateHpMax = (d, width, height, hw, widthspl) => {
    const Whalf = widthspl / 2;
    const HC = height - hw;
    const R = (Math.pow(Whalf, 2) + Math.pow(HC, 2)) / (2 * HC);
    const GamaRadians = Math.acos((width / 2) / R);
    const Gama = toDegrees(GamaRadians);
    const Hn = (width / 2) * Math.tan(toRadians(Gama));
    const PhiRadians = Math.acos(d / R);
    const Phi = toDegrees(PhiRadians);
    const Hp1 = d * Math.tan(toRadians(Phi));
    const Hp2 = Hp1 - Hn;
    const HpMax = hw + Hp2;
    return parseFloat(HpMax.toFixed(1));
  };

  /**
   * Greatest height the tunnel reaches above a face's known point.
   *
   * On the centreline this is the full tunnel height; at an offset the crown
   * has curved down, and the limit is the same Hp' the P6 path already uses.
   */
  const faceHeightLimit = (dim, n) => {
    const d = dim[`fside${n}`] === 'center' ? 0 : Number(dim[`fd${n}`]);
    if (!isFinite(d) || d <= 0) return dim.height;
    const limit = calculateHpMax(d, dim.width, dim.height, effectiveWallHeight(dim), dim.width);
    return isFinite(limit) ? limit : dim.height;
  };

  /**
   * Validate a specific field
   */
  const validateField = (fieldId) => {
    const dim = currentDimension;
    let error = "";

    switch (fieldId) {
      case 'direction':
        if (dim.direction < 0 || dim.direction > 360) {
          error = "Please choose value between 0 and 359";
        }
        break;
      case 'inclination':
        if (dim.inclination === undefined || dim.inclination === null || dim.inclination === "") {
          error = "Please enter a value (0 for horizontal tunnel)";
        } else if (!Number.isFinite(Number(dim.inclination))) {
          error = "Please enter a whole number between -89 and 89";
        } else if (dim.inclination < -89 || dim.inclination > 89) {
          error = "Value must be between -89 and 89";
        } else if (dim.inclination % 1 !== 0) {
          error = "Value must be an integer";
        }
        break;
      case 'height':
        if ((dim.height < 2 || dim.height > 100) && !heightConfirm) {
          error = dim.height > 100
            ? "Tunnel height exceeds 100 m — confirm or change"
            : "Height value unusual, confirm or change";
          setHeightConfirm(true);
        }
        break;
      case 'width':
        if ((dim.width < 2 || dim.width > 40) && !widthConfirm) {
          error = "Width value unusual, confirm or change";
          setWidthConfirm(true);
        }
        break;
      case 'hw':
        if (dim.hw < dim.height - dim.width / 2) {
          error = "Value cannot be less than " + (dim.height - dim.width / 2);
        } else if (dim.hw > dim.height) {
          error = "Value cannot be > tunnel height " + dim.height;
        }
        break;
      case 'startrd':
        if (dim.startrd < 0) {
          error = "Value cannot be < 0";
        }
        break;
      case 'endrd':
        if (dim.endrd <= dim.startrd) {
          error = "Value cannot be <= " + dim.startrd;
        } else if (isShortLog(dim.startrd, dim.endrd)) {
          error = shortLogError(dim.startrd);
        }
        break;
      case 'dir':
        if (dim.dir < 0 || dim.dir > 360) {
          error = "Value must be between 0 and 360";
        } else if (dim.dir % 1 !== 0) {
          error = "Value must be an integer";
        }
        // A strike parallel to the tunnel (horizontal trace) is no longer an
        // input error — applyHorizontalTrace nudges Dir in the renderers and
        // surfaces the soft "trace modified slightly" alert.
        break;
      case 'amt':
        if (dim.amt % 1 !== 0 || dim.amt < 0 || dim.amt > 90) {
          error = "Value must be an integer between 0 and 90";
        }
        // A 0° dip amount (flat-lying plane) is allowed — the trace plots
        // horizontal and is nudged slightly in the renderer. The soft
        // "trace modified slightly" alert is surfaced on advance (see tryAdvance).
        break;
      case 'orientationsource':
        if (!dim.orientationsource) {
          error = "Please select a value";
        }
        break;
      case 'fch1':
        if (dim.fch1 === undefined || dim.fch1 === null || dim.fch1 === "") {
          error = "Please enter Face-1 chainage";
        } else {
          error = traceChainageRangeError(dim, fieldId);
        }
        break;
      case 'fch2':
        if (!(dim.fch2 > dim.fch1)) {
          error = "Face-2 Chainage must be greater than Face-1 Chainage " + (dim.fch1 ?? "");
        } else {
          error = traceChainageRangeError(dim, fieldId);
        }
        break;
      case 'fside1':
      case 'fside2': {
        const n = fieldId.slice(-1);
        if (!['left', 'right', 'center'].includes(dim[fieldId])) {
          error = `Please select which side of the centreline the Face-${n} point lies on`;
        }
        break;
      }
      case 'fd1':
      case 'fd2': {
        const n = fieldId.slice(-1);
        if (dim[`fside${n}`] === 'center') break; // distance is 0 by definition
        const v = dim[fieldId];
        if (v === undefined || v === null || v === "") {
          error = `Please enter the Face-${n} distance from the centreline`;
        } else if (v < 0 || v > dim.width / 2) {
          error = "Distance must be between 0 and " + (dim.width / 2).toFixed(2) + " (half the tunnel width)";
        }
        break;
      }
      // The height is read at the known point, not on the centreline, so the
      // ceiling is the tunnel height *at that offset* — the crown curves down
      // away from the centreline. Validating against the full height would
      // accept a point outside the tunnel and the derived P6 would then fail
      // its own Hp check.
      case 'fh1':
      case 'fh2': {
        const v = dim[fieldId];
        const limit = faceHeightLimit(dim, fieldId.slice(-1));
        if (v === undefined || v === null || v === "" || v < 0 || v > limit) {
          error = "Value must be between 0 and " + limit + (limit < dim.height ? " (tunnel height at this offset)" : "");
        }
        break;
      }
      case 'fappdip':
        if (!(dim.fappdip >= 0 && dim.fappdip <= 90)) {
          error = "Value must be between 0 and 90";
        }
        break;
      case 'ftracedir':
        if (dim.ftracedir !== 'risesright' && dim.ftracedir !== 'fallsright') {
          error = "Please select the trace direction";
        }
        break;
      case 'p2':
      case 'p4':
      case 'p5':
        if (dim[fieldId] === undefined || dim[fieldId] === null || dim[fieldId] === "") {
          error = "Please enter a value";
        } else {
          error = traceChainageRangeError(dim, fieldId);
        }
        break;
      case 'orientation':
        if (!dim.orientation || dim.orientation === "") {
          error = "Please select a value";
        }
        break;
      case 'tunnelside':
        if (!dim.tunnelside) {
          error = "Please select the side of tunnel";
        }
        break;
      case 'rd':
        if (dim.rd > dim.endrd || dim.rd < dim.startrd) {
          error = "Chainage (m) must be between " + dim.startrd + " and " + dim.endrd;
        }
        break;
      case 'distancefromcenter':
        if (dim.distancefromcenter < 0 || dim.distancefromcenter > dim.width / 2) {
          error = "Distance must be between 0 and " + (dim.width / 2).toFixed(2);
        }
        break;
      case 'verticalheight':
        if (!dim.verticalheight || dim.verticalheight < 0) {
          error = "Vertical height must be >= 0";
        } else {
          let d = dim.distancefromcenter || 0.01;
          if (d === 0) d = 0.01;
          const HpMax = calculateHpMax(d, dim.width, dim.height, effectiveWallHeight(dim), dim.width);
          if (dim.verticalheight > HpMax) {
            error = "Vertical height cannot be greater than " + HpMax.toFixed(2);
          }
        }
        break;
      default:
        break;
    }

    const previous = fieldErrorToasts.current[fieldId];
    if (previous) {
      removeToast(previous);
      delete fieldErrorToasts.current[fieldId];
    }
    if (error) {
      if (window.audio) window.audio.play();
      addToast(error, {
        appearance: "error",
      }, (id) => {
        fieldErrorToasts.current[fieldId] = id;
      });
      return false;
    }
    return true;
  };

  /**
   * Handle field value change and auto-advance
   */
  const handleFieldChange = (e, fieldId) => {
    const name = e.target.name;
    let value = e.target.value;

    if (name === 'inclination' && !/^-?\d*$/.test(value)) return;

    props.updatedimension({ target: { name, value } });

    // Centre IS zero distance; write it so the locked box shows the value the
    // maths will use rather than whatever offset was typed before.
    if (name === 'tunnelside' && value === 'center') {
      props.updatedimension({ target: { name: 'distancefromcenter', value: 0 } });
    }
    if ((name === 'fside1' || name === 'fside2') && value === 'center') {
      props.updatedimension({ target: { name: `fd${name.slice(-1)}`, value: 0 } });
    }

    // Keep Dip Direction / Dip Amount in sync with the selected orientation
    // computation. Only source-input changes recompute, so a manual tweak of
    // dir/amt sticks until a source input changes again.
    const isStringField =
      name === 'ftracedir' || name === 'fside1' || name === 'fside2';
    const merged = { ...currentDimension, [name]: isStringField ? value : Number(value) };
    const source = merged.orientationsource || 'known';
    const changesGeometry = shapeFields.some(field => field.name === name) && Number.isFinite(Number(value));
    let result = null;
    if (source === 'facelogs' && (FACELOG_FIELD_NAMES.includes(name) || changesGeometry) && faceLogInputsReady(merged)) {
      result = computeOrientationFromFaceLogs(merged);
    } else if (source === 'chainages' && (TRACE_CHAINAGE_FIELD_NAMES.includes(name) || changesGeometry) && chainageInputsReady(merged)) {
      result = computeOrientationFromChainages(withDshapeSection(merged));
    }
    if (result) {
      props.updatedimension({ target: { name: 'dir', value: result.dir } });
      props.updatedimension({ target: { name: 'amt', value: result.amt } });
      // The box shows whole degrees; the trace is plotted from the computed
      // figure, since rounding a bearing walks the trace along the log.
      props.updatedimension({ target: { name: 'dirExact', value: result.dirExact } });
      props.updatedimension({ target: { name: 'amtExact', value: result.amtExact } });
    }

    // A hand-typed orientation has no exact counterpart, and a stale one must
    // not outlive it: the renderer prefers the exact pair wherever it exists.
    // (Source switches clear theirs in applySourceChoice.)
    const clearsExact = name === 'dir' || name === 'amt';
    if (clearsExact) {
      props.updatedimension({ target: { name: 'dirExact', value: '' } });
      props.updatedimension({ target: { name: 'amtExact', value: '' } });
    }

    // Face logs and known chainages carry their own data location; derive it
    // here so the user is never asked for a value the orientation inputs have
    // already fixed.
    const touchesSource =
      FACELOG_FIELD_NAMES.includes(name) ||
      TRACE_CHAINAGE_FIELD_NAMES.includes(name) ||
      FACE_POINT_FIELD_NAMES.includes(name);
    if (!touchesSource || !sourceDerivesLocation(source)) return;

    const location = deriveDataLocation(merged);
    if (location) {
      Object.entries(location).forEach(([key, val]) => {
        props.updatedimension({ target: { name: key, value: val } });
      });
    }
  };

  /**
   * Handle Enter key to validate and advance
   */
  const handleKeyDown = (e, fieldId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryAdvance(fieldId);
    }
  };

  /**
   * Try to advance to next field after validation
   */
  const tryAdvance = (fieldId) => {
    if (validateField(fieldId)) {
      if (fieldId === 'fd1' || fieldId === 'fd2') {
        const n = fieldId.slice(-1);
        const v = currentDimension[fieldId];
        if (['left', 'right'].includes(currentDimension[`fside${n}`]) && v !== '' && v !== null && v !== undefined && Number(v) === 0) {
          if (window.audio) window.audio.play();
          addToast(FACE_POINT_ON_CENTRELINE_ALERT, { appearance: "warning" });
        }
      }
      // A horizontal plane has an inclined trace in an inclined tunnel.
      if (fieldId === 'amt' && Number(currentDimension.amt) === 0) {
        if (window.audio) window.audio.play();
        addToast(Number(currentDimension.inclination) ? FLAT_INCLINED_ALERT : FLAT_PARALLEL_NOTE, {
          appearance: "warning",
        });
      }

      if (fieldId === 'dir' &&
          alphaBetween(Number(currentDimension.direction) % 360, Number(currentDimension.dir) % 360) === 0) {
        if (window.audio) window.audio.play();
        addToast(ALPHA0_STRIKE_NOTE, { appearance: "warning" });
      }

      // A named data location fixes the section point on its own; otherwise
      // the point is known once the last field is in.
      const namedLocation = visibleFields.some(f => f.id === 'orientation') &&
        currentDimension.orientation !== 'otherpoint';
      const locationSettledBy = namedLocation ? 'orientation' : finalLocationField(visibleFields).id;
      if (fieldId === locationSettledBy) {
        inputTraceAlerts(currentDimension).forEach((message) => addToast(message, {
          appearance: "warning",
        }));
      }

      if (fieldId === 'hw' && Number(currentDimension.hw) === Number(currentDimension.height)) {
        if (window.audio) window.audio.play();
        addToast(BOX_SECTION_ALERT, { appearance: "warning" });
      }

      // Absolute chainage values may be anything (e.g. 5700-5800); only the log
      // *length* matters. Past ~150 m the unrolled log is hard to read, so warn
      // (non-blocking) when End - Start exceeds the recommended maximum. The
      // trace still plots — the user is just nudged to trim the End Chainage.
      if (fieldId === 'endrd' &&
          exceedsMaxLogLength(currentDimension.startrd, currentDimension.endrd)) {
        if (window.audio) window.audio.play();
        addToast(LOG_LENGTH_ALERT, {
          appearance: "warning",
        });
      }

      // Mark field as complete
      setCompletedFields(prev => new Set([...prev, fieldId]));

      // Move to next field
      if (activeFieldIndex < visibleFields.length - 1) {
        setActiveFieldIndex(activeFieldIndex + 1);
      }
    }
  };

  /**
   * Handle click on a field: allow previous fields or immediate next only
   */
  const handleFieldClick = (index) => {
    if (isEditMode || index <= activeFieldIndex + 1) {
      setActiveFieldIndex(index);
    }
  };

  /**
   * Check if all required fields are complete
   */
  const isAllComplete = () => {
    return visibleFields.every(field => completedFields.has(field.id));
  };

  const getFirstInvalidFieldIndex = () => {
    for (let i = 0; i < visibleFields.length; i++) {
      if (!validateField(visibleFields[i].id)) {
        return i;
      }
    }
    return -1;
  };

  const handleNext = () => {
    if (isEditMode) {
      const firstInvalidIndex = getFirstInvalidFieldIndex();
      if (firstInvalidIndex >= 0) {
        setActiveFieldIndex(firstInvalidIndex);
        return;
      }
      setIsEditMode(false);
      props.showoutput();
      return;
    }

    if (activeField && !completedFields.has(activeField.id)) {
      if (!validateField(activeField.id)) {
        return;
      }
      setCompletedFields(prev => new Set([...prev, activeField.id]));
    }

    const firstInvalidIndex = getFirstInvalidFieldIndex();
    if (firstInvalidIndex >= 0) {
      setActiveFieldIndex(firstInvalidIndex);
      return;
    }

    props.showoutput();
  };

  /**
   * Handle Edit button - enable all fields
   */
  const handleEdit = () => {
    setIsEditMode(true);
    setActiveFieldIndex(0);
    setCompletedFields(new Set(visibleFields.map(f => f.id)));
  };

  /**
   * Handle Reset Values button - zero all inputs and restart guidance
   */
  // Reset Values asks first; only a confirmed reset clears the form.
  const [confirmingReset, setConfirmingReset] = useState(false);

  const handleReset = () => {
    props.resetvalues();
    setHeightConfirm(false);
    setWidthConfirm(false);
    setIsEditMode(false);
    setActiveFieldIndex(0);
    setCompletedFields(new Set());
  };

  /**
   * Get dynamic label for RD field based on orientation
   */
  const getRdLabel = () => {
    const orientation = currentDimension.orientation || "leftinvert";
    const labels = {
      rightinvert: "Chainage (m) at right " + (props.dimensions[0].iscrown ? "SPL" : "invert"),
      rightspl: "Chainage (m) at right SPL",
      leftspl: "Chainage (m) at left SPL",
      leftinvert: "Chainage (m) at left invert",
      centerline: "Chainage (m) at center line",
      otherpoint: "Chainage (m) of discontinuity",
    };
    return labels[orientation] || "Chainage (m)";
  };

  /**
   * Get field state CSS class
   */
  const getFieldClass = (index, fieldId) => {
    if (isEditMode) {
      return index === activeFieldIndex
        ? 'guided-field guided-field--active'
        : 'guided-field guided-field--complete';
    }
    if (index === activeFieldIndex) {
      return 'guided-field guided-field--active';
    }
    if (completedFields.has(fieldId)) {
      return 'guided-field guided-field--complete';
    }
    return 'guided-field guided-field--pending';
  };

  /**
   * One-line account of what the selected source derived, shown in place of the
   * inputs it replaced so the values are never silently applied.
   */
  const buildDerivedSummary = () => {
    if (currentDimension.orientationsource !== 'facelogs') return null;
    const location = deriveDataLocation(currentDimension);
    if (!location) return null;
    // Name the anchoring face: the location comes from Face-1's known point.
    const detail =
      location.orientation === 'otherpoint'
        ? `data point from the Face-1 known point — Ch. ${location.rd}, ${location.tunnelside}, ${location.distancefromcenter} m from centreline, ${location.verticalheight} m above invert`
        : `data point at Ch. ${location.rd} on the centreline`;
    return `Computed from the above — ${detail}`;
  };
  const derivedSummary = buildDerivedSummary();

  /**
   * Render a single field
   */
  const renderField = (field, index) => {
    const isActive = index === activeFieldIndex;
    const isComplete = completedFields.has(field.id);
    const isReadOnly = isFieldReadOnly(field);

    // Get label (dynamic for rd field)
    const label = field.dynamicLabel ? getRdLabel() : field.label;

    return (
      <div key={field.id} className="guided-field-wrap">
        {/* A read-only field takes no input, so it is never prompted for one. */}
        {isActive && !isComplete && !isReadOnly && (
          <span className="input-please">Input Please</span>
        )}
        {isActive && !isComplete && isReadOnly && (
          <span className="input-computed">Computed — not editable</span>
        )}
        <div
          className={getFieldClass(index, field.id)}
          onClick={() => handleFieldClick(index)}
        >
          <div className="guided-field__label">
            <span>{label}</span>
          </div>

          {field.type === 'select' ? (
            <select
              ref={el => inputRefs.current[field.id] = el}
              id={field.id}
              name={field.name}
              className="guided-field__select"
              value={currentDimension[field.name] || ''}
              onChange={(e) => handleFieldChange(e, field.id)}
              onKeyDown={(e) => handleKeyDown(e, field.id)}
              disabled={isReadOnly}
            >
              {field.options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              ref={el => inputRefs.current[field.id] = el}
              type={field.type}
              id={field.id}
              name={field.name}
              className="guided-field__input"
              value={currentDimension[field.name] ?? ''}
              onChange={(e) => handleFieldChange(e, field.id)}
              onKeyDown={(e) => handleKeyDown(e, field.id)}
              step={field.step}
              inputMode={field.inputMode}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              readOnly={isReadOnly}
            />
          )}
        </div>

        {/* Hint for specific fields */}
        {field.id === 'direction' && (
          <small className="guided-field-hint">
            Clockwise from North, in the direction of increasing chainage (000–359).
          </small>
        )}
        {field.id === 'inclination' && (
          <small className="guided-field-hint">
            Positive when the tunnel falls and negative when it rises in the direction of increasing chainage.
            Whole degrees, -89 to 89; enter 0 for a horizontal tunnel.
          </small>
        )}
        {(field.id === 'tunnelside' || field.id === 'fside1' || field.id === 'fside2') && (
          <small className="guided-field-hint">
            Right means the right-hand side looking towards increasing chainage.
          </small>
        )}
        {(field.id === 'dir' || field.id === 'amt') && orientationIsComputed && (
          <small className="guided-field-hint">
            Computed from the inputs above and not editable here; shown rounded to the
            whole degree, while the trace is plotted from the exact value.
          </small>
        )}
        {field.id === 'rd' && currentDimension.orientation === 'otherpoint' && (
          <small className="guided-field-hint">
            Must be between {currentDimension.startrd} and {currentDimension.endrd}
          </small>
        )}
        {field.id === 'distancefromcenter' && (
          <small className="guided-field-hint">
            Must be ≤ {(currentDimension.width / 2).toFixed(2)} (half the tunnel width)
          </small>
        )}
        {(field.id === 'fd1' || field.id === 'fd2') &&
          currentDimension[`fside${field.id.slice(-1)}`] !== 'center' && (
            <small className="guided-field-hint">
              Must be ≤ {(currentDimension.width / 2).toFixed(2)} (half the tunnel width). Any point on the
              trace will do — it need not match the other face.
            </small>
          )}
        {field.id === 'fside1' && (
          <small className="guided-field-hint">
            Both known points are plotted as discontinuity data locations.
          </small>
        )}
        {field.id === 'dlabel' && (
          <small className="guided-field-hint">
            Optional. Your name for this discontinuity, shown in the Inputs table.
          </small>
        )}
        {/* Show what was derived, since these are no longer asked for */}
        {field.id === 'fh2' && derivedSummary && (
          <small className="guided-field-hint">{derivedSummary}</small>
        )}
      </div>
    );
  };

  return (
    <div id="page">
      <Header onBack={props.showobjective} />
      <Footer />
      <ConfirmResetModal
        show={confirmingReset}
        onCancel={() => setConfirmingReset(false)}
        onConfirm={() => {
          setConfirmingReset(false);
          handleReset();
        }}
      />
      <div className="page-content header-clear-medium">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card card-style contact-form">
              <div className="content guided-uniform">
                {/* Trace selector */}
                {props.tracecount > 0 && (
                  <div className="guided-field-wrap mb-3">
                    <div className="guided-field guided-field--complete">
                      <div className="guided-field__label">
                        <span>Select Trace</span>
                      </div>
                      <select
                        onChange={updateCurrentTrace}
                        value={props.currenttrace}
                        className="guided-field__select"
                      >
                        {props.dimensions.map((dimension, index) => (
                          <option
                            style={{ color: colors[index] }}
                            key={index}
                            value={index}
                          >
                            Trace {index + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Chosen on the previous screen; reported here with a way back. */}
                <div className="guided-field-wrap mb-3">
                  <div className="guided-field guided-field--complete">
                    <div className="guided-field__label">
                      <span>Discontinuity Orientation</span>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ padding: '8px 14px', gap: '10px' }}
                    >
                      <span style={{ lineHeight: 1.3 }}>{sourceLabel}</span>
                      {props.onChangeSource && (
                        <button
                          type="button"
                          className="btn btn-xxs bg-secondary rounded-sm text-uppercase"
                          style={{ flexShrink: 0 }}
                          onClick={props.onChangeSource}
                        >
                          Change
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Render all visible fields */}
                {visibleFields.map((field, index) => renderField(field, index))}

                {/* Action buttons */}
                <div className="guided-input-actions mt-4">
                  <button
                    type="button"
                    className="btn bg-red-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={() => setConfirmingReset(true)}
                  >
                    Reset Values
                  </button>
                  <button
                    type="button"
                    className="btn bg-secondary text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn bg-green-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleNext}
                    disabled={!isEditMode && !isAllComplete()}
                  >
                    {props.label || "Next"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Input;
